import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Download,
  Printer,
  FileText,
  Paperclip,
  ChevronDown,
  ChevronUp,
  CheckCircle,
  AlertCircle,
  X
} from 'lucide-react';
import { getRevenue } from '../../../../services/finance_services/revenueService';
import { getRevenueAttachments, downloadRevenueAttachment } from '../../../../services/finance_attachment_services/revenueAttachmentService';
import { exportRevenuePDF, exportRevenueExcel } from '../../../../services/export_services/revenueExportService';
import { formatDate, formatCurrency } from '../../../../utils/formatUtils';
import useApi from '../../../../hooks/useApi';

const RevenueDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [revenue, setRevenue] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [notification, setNotification] = useState(null);

  // Use the useApi hook for fetching revenue
  const { loading, error, callApi: fetchRevenueData } = useApi(getRevenue, {showSuccessToast: false});

  useEffect(() => {
    const fetchData = async () => {
      try {
        const revenueData = await fetchRevenueData(id);
        if (revenueData) {
          setRevenue(revenueData);
        }

        // Fetch attachments separately
        const attachmentsData = await getRevenueAttachments(id);
        setAttachments(attachmentsData?.data || []);
      } catch (err) {
        console.error('Error fetching data:', err);
      }
    };

    fetchData();
  }, [id, fetchRevenueData]);

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      const result = await exportRevenuePDF(id);
      
      if (!result.success) {
        throw new Error(result.error || 'Export failed');
      }
      
      setNotification({ type: 'success', message: 'PDF download successful!' });
      setTimeout(() => setNotification(null), 3000);
    } catch (err) {
      setNotification({ type: 'error', message: err.message || 'PDF download failed' });
      setTimeout(() => setNotification(null), 3000);
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadExcel = async () => {
    setDownloading(true);
    try {
      const result = await exportRevenueExcel(id);
      
      if (!result.success) {
        throw new Error(result.error || 'Export failed');
      }
      
      setNotification({ type: 'success', message: 'Excel download successful!' });
      setTimeout(() => setNotification(null), 3000);
    } catch (err) {
      setNotification({ type: 'error', message: err.message || 'Excel download failed' });
      setTimeout(() => setNotification(null), 3000);
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadAttachment = async (attachmentId) => {
    try {
      setNotification({ type: 'loading', message: 'Downloading attachment...' });
      const result = await downloadRevenueAttachment(id, attachmentId);
      
      if (!result.success) {
        throw new Error(result.error || 'Download failed');
      }
      
      setNotification({ type: 'success', message: 'Download successful!' });
      setTimeout(() => setNotification(null), 3000);
    } catch (err) {
      setNotification({ type: 'error', message: err.message || 'Download failed' });
      setTimeout(() => setNotification(null), 3000);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-700"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 p-6 rounded-lg flex items-center">
        <AlertCircle className="h-6 w-6 mr-3 flex-shrink-0" />
        <div>
          <h3 className="font-medium">Error loading revenue details</h3>
          <p className="text-sm text-red-600">{error}</p>
          <button 
            onClick={() => navigate('/dashboard/revenue-reports')}
            className="mt-3 text-sm text-primary-600 hover:text-primary-800"
          >
            Return to revenue list
          </button>
        </div>
      </div>
    );
  }

  if (!revenue) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 p-6 rounded-lg">
        <h3 className="font-medium">Revenue not found</h3>
        <p className="text-sm">The revenue you're looking for doesn't exist or has been deleted.</p>
        <button 
          onClick={() => navigate('/dashboard/revenue-reports')}
          className="mt-3 text-sm text-primary-600 hover:text-primary-800"
        >
          Return to revenue list
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Notification toast */}
      {notification && (
        <div className={`fixed top-5 right-5 z-50 flex items-center p-4 rounded-lg shadow-md ${
          notification.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' :
          notification.type === 'error' ? 'bg-red-50 text-red-800 border border-red-200' :
          'bg-blue-50 text-blue-800 border border-blue-200'
        }`}>
          {notification.type === 'success' && <CheckCircle className="h-5 w-5 mr-2" />}
          {notification.type === 'error' && <AlertCircle className="h-5 w-5 mr-2" />}
          {notification.type === 'loading' && 
            <div className="h-5 w-5 mr-2 border-2 border-t-primary-600 border-r-primary-600 border-b-primary-100 border-l-primary-100 rounded-full animate-spin"></div>
          }
          <span>{notification.message}</span>
          <button 
            onClick={() => setNotification(null)}
            className="ml-3 text-gray-400 hover:text-gray-600"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Header with back button and actions */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <button
          onClick={() => navigate('/dashboard/revenue-reports')}
          className="inline-flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Revenues
        </button>
        
        <div className="flex gap-3">
          <button
            onClick={handleDownloadPDF}
            disabled={downloading}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-75"
          >
            {downloading ? (
              <>
                <div className="h-4 w-4 mr-2 border-2 border-t-primary-300 border-r-primary-300 border-b-white border-l-white rounded-full animate-spin"></div>
                Downloading...
              </>
            ) : (
              <>
                <Download className="h-4 w-4 mr-2" />
                Download PDF
              </>
            )}
          </button>
          
          <button
            onClick={handleDownloadExcel}
            disabled={downloading}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-75"
          >
            {downloading ? (
              <>
                <div className="h-4 w-4 mr-2 border-2 border-t-gray-300 border-r-gray-300 border-b-white border-l-white rounded-full animate-spin"></div>
                Downloading...
              </>
            ) : (
              <>
                <FileText className="h-4 w-4 mr-2" />
                Download Excel
              </>
            )}
          </button>
          
          <button 
            onClick={() => window.print()}
            className="inline-flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <Printer className="h-4 w-4 mr-2" />
            Print
          </button>
        </div>
      </div>

      {/* Report Preview Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 md:p-6 print:shadow-none print:border-none">
        {/* Report Header */}
        <div className="border-b border-gray-200 pb-4 mb-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-gray-900">{revenue.title}</h2>
              <p className="text-sm text-gray-500">Received on {formatDate(revenue.date_received)}</p>
            </div>
            <div className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-xs font-medium flex-shrink-0">
              {revenue.category_name}
            </div>
          </div>
        </div>

        {/* Report Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-medium text-gray-500">Amount</h3>
              <p className="text-2xl font-bold">{formatCurrency(revenue.amount, 'FCFA')}</p>
            </div>
            
            <div>
              <h3 className="text-sm font-medium text-gray-500">Date Received</h3>
              <p className="text-base">{formatDate(revenue.date_received)}</p>
            </div>
            
            <div>
              <h3 className="text-sm font-medium text-gray-500">Client</h3>
              <p className="text-base">{revenue.client || 'Not specified'}</p>
            </div>
          </div>
          
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-medium text-gray-500">Payment Method</h3>
              <p className="text-base">{revenue.payment_method || 'Not specified'}</p>
            </div>
            
            <div>
              <h3 className="text-sm font-medium text-gray-500">Reference Number</h3>
              <p className="text-base">{revenue.reference_number || 'Not specified'}</p>
            </div>
            
            <div>
              <h3 className="text-sm font-medium text-gray-500">Last Modified</h3>
              <p className="text-base">{formatDate(revenue.date_modified)}</p>
            </div>
          </div>
        </div>

        {/* Description Section */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-2">
            <h3 className="text-sm font-medium text-gray-500">Description</h3>
            {revenue.description && revenue.description.length > 100 && (
              <button 
                onClick={() => setShowFullDescription(!showFullDescription)}
                className="text-blue-600 text-sm flex items-center"
              >
                {showFullDescription ? (
                  <>
                    Show Less <ChevronUp className="h-4 w-4 ml-1" />
                  </>
                ) : (
                  <>
                    Show More <ChevronDown className="h-4 w-4 ml-1" />
                  </>
                )}
              </button>
            )}
          </div>
          <div className={`bg-gray-50 p-4 rounded-lg ${
            revenue.description && revenue.description.length > 100 && !showFullDescription 
              ? 'max-h-24 overflow-hidden' 
              : ''
          }`}>
            <p className="text-gray-700 whitespace-pre-line">
              {revenue.description || 'No description provided'}
            </p>
          </div>
        </div>

        {/* Attachments Section */}
        <div>
          <h3 className="text-lg font-medium mb-4">Attachments</h3>
          {attachments.length === 0 ? (
            <div className="bg-gray-50 p-8 text-center rounded-lg">
              <FileText className="mx-auto h-12 w-12 text-gray-400 mb-3" />
              <p className="text-gray-500">No attachments for this revenue</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {attachments.map(attachment => (
                <div key={attachment.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start">
                    <Paperclip className="h-5 w-5 text-gray-400 mt-0.5 mr-3 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{attachment.title}</p>
                      <p className="text-xs text-gray-500">
                        {attachment.file_size 
                          ? `${Math.round(attachment.file_size / 1024)} KB` 
                          : 'Size unknown'}
                      </p>
                      <button
                        onClick={() => handleDownloadAttachment(attachment.id)}
                        className="mt-2 inline-flex items-center text-sm text-blue-600 hover:text-blue-800"
                      >
                        <Download className="h-4 w-4 mr-1" />
                        Download
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RevenueDetailPage;